import debounce from 'lodash/debounce';
import React, { FC, useEffect, useState } from 'react';

import {
    Category,
    CategoryList,
    fetchFilterForCrossCampaignCategory,
    setPreOpenCategoriesFilter,
} from '@browsing/filters';
import { storageSaveData } from '@browsing/utils';
import { track } from '@client/helpers/tracking';
import { GridPageLayout } from '@lux/grid';
import { Preset, Text } from '@lux/text';
import { Title, TitlePreset } from '@lux/title';
import { Translate, TranslateScope } from '@pegasus/translation';

import * as S from './cross-campaign-entry.styles';
import { getSelectedCategories, getTrackingCategoryFilterValues } from './helpers';

const categorySortIdToImageMapping = {
    1: 'https://img01.ztat.net/zl-campaignC/cross_campaign/Women-v2.jpg',
    2: 'https://img01.ztat.net/zl-campaignC/cross_campaign/Men-v2.jpg',
    3: 'https://img01.ztat.net/zl-campaignC/cross_campaign/Girl-v2.jpg',
    4: 'https://img01.ztat.net/zl-campaignC/cross_campaign/Boy-v2.jpg',
    5: 'https://img01.ztat.net/zl-campaignC/cross_campaign/Home-v2.jpg',
};

function saveFiltersToSessionStorage(categoryId: number) {
    storageSaveData({
        key: 'filters',
        subKey: 'cross-campaign',
        payload: {
            brands: [],
            categories: [categoryId],
            colors: [],
            materials: [],
            prices: [] as any,
            sizes: {},
        },
        overwrite: false,
        storageType: 'sessionStorage',
    });
}

interface LevelOneCategorySelectionProps {
    levelOneCategories: Category[];
    onLevelOneCategoryClick: (categoryId: number) => void;
    onContainerScroll: () => void;
}

const LevelOneCategorySelection: FC<LevelOneCategorySelectionProps> = ({
    levelOneCategories,
    onLevelOneCategoryClick,
    onContainerScroll,
}) => (
    <S.LevelOneCategorySelectionWrapper onScroll={onContainerScroll}>
        {levelOneCategories.map(({ sortId, name, id }: Category) => {
            const categoryImage = categorySortIdToImageMapping[sortId];

            return !categoryImage ? null : (
                <S.LevelOneCategory key={sortId}>
                    <S.LevelOneCategoryImage
                        src={categoryImage}
                        alt={name}
                        onClick={() => onLevelOneCategoryClick(id)}
                    />
                    <Text preset={Preset.XS}>{name}</Text>
                </S.LevelOneCategory>
            );
        })}
    </S.LevelOneCategorySelectionWrapper>
);

const Heading = () => (
    <S.HeadingWrapper>
        <TranslateScope path="myLounge.crossCampaign.entryPoint">
            <Title preset={TitlePreset.S}>
                <Translate message="title" />
            </Title>
        </TranslateScope>
    </S.HeadingWrapper>
);

interface CrossCampaignEntryProps {
    isCrossCampaignExperimentEnabled: boolean;
    isXsScreen: boolean;
}

export const CrossCampaignEntry: FC<CrossCampaignEntryProps> = ({
    isCrossCampaignExperimentEnabled,
    isXsScreen,
}) => {
    const [allCategories, setAllCategories] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState(null);

    useEffect(() => {
        const fetchFilterCategories = async () => {
            try {
                const categories = await fetchFilterForCrossCampaignCategory();
                setAllCategories(categories.category_filter);
                track('crossCampaignFilter_lvl1_shown');
            } catch (e) {
                console.log(e);
            }
        };
        fetchFilterCategories();
    }, []);

    const updateFilter = (filter: any) => {
        const categoryId = filter.categories[0];
        const selected = getSelectedCategories(categoryId, allCategories);
        setSelectedCategories(selected);
    };

    const redirectToArticlesPage = (categoryId: number) => {
        track(`crossCampaignFilter_lvl1_apply`, {
            filterValues: '1',
        });

        track('crossCampaignFilter_lvl1_click', {
            filterValues: categoryId,
        });

        saveFiltersToSessionStorage(categoryId);

        setPreOpenCategoriesFilter(true);

        window.location.href = `/articles/categories/${[categoryId]}`;
    };

    const setVisibilityFilter = (_: string, isWithoutHashMeta: boolean, shouldSave?: boolean) => {
        if (shouldSave === true) {
            const filterValues = getTrackingCategoryFilterValues(
                selectedCategories.parentId,
                allCategories,
                [selectedCategories.id],
            );

            track(`crossCampaignFilter_lvl${filterValues.length}_apply`, {
                filterValues: filterValues.join(';'),
            });

            saveFiltersToSessionStorage(selectedCategories.id);

            window.location.href = `/articles/categories/${selectedCategories.id}`;
        } else {
            setSelectedCategories(null);
        }
    };

    if (!allCategories) {
        return null;
    }

    const handleClick = (category: any) => {
        setSelectedCategories(category);
        track('crossCampaignFilter_lvl1_click', {
            filterValues: category.id,
        });
        track('crossCampaignFilter_lvl2_shown');
    };

    const handleScroll = debounce(() => {
        track('crossCampaignFilter_lvl1_swipe');
    }, 100);

    const onToggleSelection = (depth: number, isExpanded: boolean) => {
        if (depth === 2) {
            if (isExpanded) {
                track('crossCampaignFilter_lvl2_shown');
            } else {
                track('crossCampaignFilter_lvl3_shown');
            }
        }
    };

    return isCrossCampaignExperimentEnabled && !isXsScreen ? (
        <GridPageLayout>
            <S.EntryPointWrapper>
                <Heading />
                <LevelOneCategorySelection
                    levelOneCategories={allCategories.categories}
                    onLevelOneCategoryClick={redirectToArticlesPage}
                    onContainerScroll={handleScroll}
                />
            </S.EntryPointWrapper>
        </GridPageLayout>
    ) : (
        <S.CrossCampaignContainer>
            <TranslateScope path="catalog">
                {selectedCategories && (
                    <CategoryList
                        allCategories={allCategories}
                        availableCategories={allCategories}
                        selectedCategories={[selectedCategories.id]}
                        campaign={null}
                        setVisibilityFilter={setVisibilityFilter}
                        showCategoriesModal={true}
                        updateFilter={updateFilter}
                        totalArticlesCount={selectedCategories.count}
                        isResetHidden={true}
                        disableRootCategoryToggle={true}
                        isCrossCampaign={true}
                        onToggleSelection={onToggleSelection}
                        isMyLoungeCrossCampaign={true}
                    />
                )}
            </TranslateScope>
            <S.StyledTitle preset={TitlePreset.XS}>
                <Translate message="crossCampaign.entryPoint.title" />
            </S.StyledTitle>
            <S.Container onScroll={handleScroll}>
                {allCategories.categories.map(
                    (category: Category) =>
                        categorySortIdToImageMapping[category.sortId] && (
                            <S.Wrapper key={category.sortId}>
                                <S.StyledImageWrapper onClick={() => handleClick(category)}>
                                    <S.StyledImage
                                        src={categorySortIdToImageMapping[category.sortId]}
                                        alt={category.name}
                                    />
                                </S.StyledImageWrapper>
                                <Text preset={Preset.XS}>{category.name}</Text>
                            </S.Wrapper>
                        ),
                )}
            </S.Container>
        </S.CrossCampaignContainer>
    );
};
